import { PlusCircleOutlined, RightOutlined } from '@ant-design/icons';
import { Button, notification, Space, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { ICountryLookupListTableRow } from '../../../../../interfaces/lookup/country';
import { ICurrency } from '../../../../../interfaces/lookup/currency/data';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { NOTIFICATION_PLACEMENT } from '../../../../../shared/Constants';
import { AppDispatch, RootState } from '../../../../../store';
import { fetchCountries } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';

export const CountryLookupListOverview = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { countries } = useSelector((state: RootState) => state.lookupSlice);

    const columns: ColumnsType<ICountryLookupListTableRow> = [
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            render: (_, record) => (
                <>
                    <Tooltip title={record.description}>{record.code}</Tooltip>
                </>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Default currency',
            dataIndex: 'defaultCurrency',
            key: 'defaultCurrency',
            render: (defaultCurrency: ICurrency) => (
                <>
                    {defaultCurrency && defaultCurrency.code && (
                        <Tooltip title={defaultCurrency.description}>{defaultCurrency.code}</Tooltip>
                    )}
                </>
            ),
        },
        {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 100,
            render: (_, record) => (
                <Space size="middle">
                    <Button type="text" icon={<RightOutlined />} onClick={() => navigate(`/admin/lookup-lists/country/${record.id}`)} />
                </Space>
            ),
        },
    ];

    useEffect(() => {
        if (countries.model.updateStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/country', { replace: true });
        }
    }, [countries.model.updateStatus]);

    useEffect(() => {
        dispatch(fetchCountries());

        return () => {
            dispatch(resetState());
        };
    }, []);

    useEffect(() => {
        if (countries.model.fetchStatus === StatePieceStatus.Error) {
            notification.error({
                message: 'An error occurred.',
                duration: 0,
                description: 'An error occurred while retrieving the data. Please contact your administrator if the problem persists.',
                placement: NOTIFICATION_PLACEMENT,
            });
        }
    }, [countries.model.fetchStatus]);

    return (
        <div className="app-page">
            <ContentHeader
                title="Manage countries lookup list"
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'Countries',
                        path: '/admin/lookup-lists/country',
                    },
                ]}
            >
                <>
                    <Button type="primary" icon={<PlusCircleOutlined />} onClick={() => navigate('/admin/lookup-lists/country/add')}>
                        Add new option
                    </Button>
                </>
            </ContentHeader>

            <div className="page-content">
                <Table
                    size="small"
                    bordered={true}
                    loading={countries.lookup.fetchStatus === StatePieceStatus.IsFetching}
                    dataSource={countries.model.data.map((option, i) => {
                        return { ...option, key: i } as ICountryLookupListTableRow;
                    })}
                    columns={columns}
                    pagination={{
                        defaultPageSize: 100,
                    }}
                />
            </div>
        </div>
    );
};
